import React from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import type { NodeType, Permission } from '@/types/Permission'

import type { PermissionState, RenderOptions } from './types'

/**
 * Generate smart default tooltip messages based on resource type and permission
 */
export const getDefaultTooltipMessage = (
  resourceType: NodeType | 'organization',
  permission: Permission | Permission[],
  context: 'single' | 'bulk' = 'single',
): string => {
  const permissionStr = Array.isArray(permission)
    ? permission.join(' and ')
    : permission

  const resourceLabel = getResourceLabel(resourceType)

  if (context === 'bulk') {
    return `You need ${permissionStr} permission on all selected ${resourceLabel}s to perform this action`
  }

  if (resourceType === 'organization') {
    return `You don't have ${permissionStr} permission in this organization`
  }

  return `You don't have ${permissionStr} permission for this ${resourceLabel}`
}

/**
 * Get human-readable resource labels
 */
const getResourceLabel = (resourceType: NodeType): string => {
  switch (resourceType) {
    case 'compliance_item':
      return 'compliance item'
    default:
      return resourceType as string
  }
}

/**
 * No-op event handler that prevents default and stops propagation
 */
const preventInteraction = (e: React.SyntheticEvent) => {
  e.preventDefault()
  e.stopPropagation()
}

/**
 * Clone React element and add disabled prop.
 * Also intercepts click/select handlers to prevent them from firing,
 * since some components (like Radix DropdownMenuItem) may still
 * call onClick even when disabled.
 */
export const cloneElementWithDisabled = (
  element: React.ReactElement,
): React.ReactElement => {
  // If the component already is disabled, return it as is
  const existingDisabled = element.props.disabled
  if (existingDisabled) {
    return element
  }

  // Build override props that prevent interaction
  const overrideProps: Record<string, unknown> = {
    disabled: true,
    'aria-disabled': true,
  }

  // Intercept common interaction handlers to prevent them from firing
  // This is needed because some components don't properly respect `disabled`
  const handlersToBlock = [
    'onClick',
    'onSelect',
    'onPointerDown',
    'onMouseDown',
    'onKeyDown',
  ] as const

  for (const handler of handlersToBlock) {
    if (element.props[handler]) {
      overrideProps[handler] = preventInteraction
    }
  }

  return React.cloneElement(element, {
    ...element.props,
    ...overrideProps,
  })
}

/**
 * Clone React element and add readOnly prop
 */
export const cloneElementWithReadOnly = (
  element: React.ReactElement,
): React.ReactElement => {
  return React.cloneElement(element, {
    ...element.props,
    readOnly: true,
  })
}

/**
 * Core rendering logic shared by all PermissionGate components
 */
export const renderWithPermission = (
  permissionState: PermissionState,
  children: React.ReactNode,
  options: RenderOptions,
): React.ReactNode => {
  const { hasPermission, isLoading, isError } = permissionState

  const {
    fallback,
    fallbackComponent,
    loadingComponent = null,
    tooltip,
    tooltipProps,
    tooltipContentProps,
  } = options

  // Handle loading state without showing denied-state replacement UI.
  if (isLoading) {
    switch (fallback) {
      case 'hide':
        return loadingComponent
      case 'replace':
        return loadingComponent
      case 'disable':
        // Show disabled element without tooltip during loading
        return React.isValidElement(children)
          ? cloneElementWithDisabled(children)
          : children
      case 'readonly':
        // Show readonly element during loading to prevent flash
        return React.isValidElement(children)
          ? cloneElementWithReadOnly(children)
          : children
      default:
        return null
    }
  }

  // Error state - return null
  if (isError) {
    return null
  }

  // User has permission - render normally
  if (hasPermission) {
    return children
  }

  // User lacks permission - apply fallback behavior with tooltips
  switch (fallback) {
    case 'hide':
      return null

    case 'replace':
      return fallbackComponent || null

    case 'disable':
      const disabledElement = React.isValidElement(children)
        ? cloneElementWithDisabled(children)
        : children

      // Add tooltip if provided (only when permission is actually denied, not loading)
      if (tooltip) {
        return (
          <Tooltip {...tooltipProps}>
            <TooltipTrigger asChild>{disabledElement}</TooltipTrigger>
            <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
          </Tooltip>
        )
      }

      return disabledElement

    case 'readonly':
      const readonlyElement = React.isValidElement(children)
        ? cloneElementWithReadOnly(children)
        : children

      return readonlyElement

    default:
      return null
  }
}

/**
 * Validate that children is a single React element when asChild is true
 */
export const validateAsChildProps = (
  children: React.ReactNode,
  asChild: boolean,
): void => {
  if (asChild && !React.isValidElement(children)) {
    throw new Error(
      'PermissionGate: asChild prop requires children to be a single React element',
    )
  }
}
